<template>
  <div>
    <template v-if="isVideoCallBlank">
      <VCard
        flat
        class="card-video-links d-flex flex-column justify-end grey lighten-4 fill-height"
      >
        <div class="card-video-links__title">
          {{ $t('videoCallExploration') }}
        </div>
        <div class="card-video-links__item mb-7">
          <div class="card-video-links__item-label">
            {{ $t('candidateUrl') }}
          </div>
          <VTextField
            v-model="interview.videoCallCandidateUrl"
            readonly
            outlined
            hide-details
            flat
            dense
            background-color="#ffffff"
            height="32px"
            class="card-video-links__item-field"
          >
            <template v-slot:append>
              <VIcon class="card-video-links__item-icon" @click="copyCandidateLink">
                {{ mdiFileMultipleIcon }}
              </VIcon>
            </template>
          </VTextField>
        </div>
        <div class="card-video-links__item mb-5">
          <div class="card-video-links__item-label">
            {{ $t('adminUrl') }}
          </div>
          <VTextField
            v-model="interview.videoCallAdminUrl"
            readonly
            outlined
            hide-details
            flat
            dense
            background-color="#ffffff"
            color="#757575"
            height="32px"
            class="card-video-links__item-field"
          >
            <template v-slot:append>
              <VIcon class="card-video-links__item-icon" @click="copyAdminLink">
                {{ mdiFileMultipleIcon }}
              </VIcon>
            </template>
          </VTextField>
        </div>
        <div class="card-video-links__bottom-text">
          <div class="card-video-links__bottom-icon">
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M1 13.3291L7 1.3291L13 13.3291H7H1Z" stroke="#919191" stroke-linejoin="round" />
              <path d="M7 5.3291V9.8291" stroke="#919191" stroke-linecap="round" />
              <circle
                cx="7"
                cy="11.3291"
                r="0.5"
                fill="#919191"
              />
            </svg>
          </div>
          {{ $t('videoCallBottomText') }}
        </div>
      </VCard>
    </template>
    <CandidateInterviewProcessing v-else-if="isOfficeInterviewProcessing" />
    <CandidateInterviewProcessingFailed v-else-if="isOfficeInterviewProcessingFailed" />
    <VideoCallEmpty v-else-if="isOfficeInterviewVideoEmpty" />
    <VideoCallWithoutRecording v-else-if="isOfficeInterviewWithoutRecording" />
    <CandidateInterviewOffice
      v-else-if="showOfficeInterviewPlayer"
      v-model="officeInterviewPlayerTime"
      :interview="interview"
    />
  </div>
</template>

<script>
import copy from 'clipboard-copy';
import { mdiFileMultiple } from '@mdi/js';
import VideoCallWithoutRecording from './VideoCallWithoutRecording.vue';
import { interviewStatuses } from '../../../enums';
import CandidateInterviewProcessing from '../CandidateInterviewProcessing.vue';
import CandidateInterviewProcessingFailed from '../CandidateInterviewProcessingFailed.vue';
import CandidateInterviewOffice from '../CandidateInterviewOffice.vue';
import VideoCallEmpty from './VideoCallEmpty.vue';

export default {
  name: 'VideoCall',
  components: {
    CandidateInterviewProcessing,
    CandidateInterviewProcessingFailed,
    CandidateInterviewOffice,
    VideoCallWithoutRecording,
    VideoCallEmpty,
  },
  props: {
    candidate: {
      type: Object,
      required: true,
    },
    interview: {
      required: true,
    },
  },
  data() {
    return {
      interviewStatusType: interviewStatuses,
      mdiFileMultipleIcon: mdiFileMultiple,
    };
  },
  computed: {
    officeInterviewPlayerTime: {
      get() {
        return this.$store.state.candidatePage.officeInterviewPlayerTime;
      },
      set(value) {
        this.$store.commit('candidatePage/setOfficeInterviewPlayerTime', value);
      },
    },
    showOfficeInterviewPlayer() {
      return this.interview
        && this.interview.status === this.interviewStatusType.READY;
    },
    isVideoCallBlank() {
      return this.interview
        && (this.interview.status === this.interviewStatusType.BLANK
          || this.interview.status === this.interviewStatusType.RECORDING);
    },
    isOfficeInterviewProcessing() {
      return this.interview
        && this.interview.status === this.interviewStatusType.PROCESSING;
    },
    isOfficeInterviewProcessingFailed() {
      return this.interview
        && this.interview.status === this.interviewStatusType.PROCESSING_FAILED;
    },
    isOfficeInterviewWithoutRecording() {
      return (
        this.interview
        && this.interview.status === this.interviewStatusType.READY
        && !this.interview.video?.url
      );
    },
    isOfficeInterviewVideoEmpty() {
      return (
        this.isOfficeInterviewWithoutRecording
        && this.interview?.emptyVideo
      );
    },
  },
  methods: {
    copyCandidateLink() {
      copy(this.interview.videoCallCandidateUrl);
      this.$toast.info(this.$t('candidateLinkCopied'));
    },
    copyAdminLink() {
      copy(this.interview.videoCallAdminUrl);
      this.$toast.info(this.$t('candidateLinkCopied'));
    },
  },
};
</script>

<style lang="scss" scoped>
.card-video-links {
  padding: 1.875rem;
  border-radius: 0 0 0.5rem 0.5rem;
  &__title {
    font-size: 1.125rem;
    line-height: 1.11;
    color: #212121;
    margin-bottom: 1.25rem;
    text-align: center;
  }
  &__item {
    font-size: 0.875rem;
    line-height: 1.43;
    color: #757575;
  }
  &__item-label {
    text-align: center;
    margin-bottom: 0.3125rem;
  }
  &__item-field {
    max-width: 21.5625rem;
    font-size: 0.875rem;
    line-height: 1;
    color: #757575;
    border: 1px solid #E0E0E0;
    border-radius: 0.25rem;
    margin-left: auto;
    margin-right: auto;
  }
  &__item-icon {
    width: 1.875rem;
    height: 1.875rem;
    font-size: 0.875rem;
    align-items: center;
    justify-content: center;
    border-left: 1px solid #E0E0E0;
    svg {
      width: 0.875rem;
      height: 0.875rem;
    }
  }
  &__bottom-text {
    display: flex;
    align-items: center;
    max-width: 26.125rem;
    font-size: 0.75rem;
    line-height: 1.33;
    color: #757575;
    margin: 0 auto;
  }
  &__bottom-icon {
    margin-right: 0.625rem;
  }
}
@media #{map-get($display-breakpoints, 'sm-and-down')} {
  .card-video-links {
    padding: 1rem;
  }
}

</style>
<style lang="scss">
.card-video-links__item .v-text-field--enclosed.v-input--dense:not(.v-text-field--solo).v-text-field--outlined .v-input__append-inner, .card-video-links__item .v-text-field--enclosed.v-input--dense:not(.v-text-field--solo).v-text-field--outlined .v-input__append-outer {
  margin-top: 1px;
}
 .card-video-links__item-field.v-text-field.v-text-field--enclosed:not(.v-text-field--rounded) > .v-input__control > .v-input__slot {
   padding: 0 2px 0 12px;
   min-height: auto !important;
 }
.card-video-links__item-icon {
    svg {
      width: 0.875rem;
      height: 0.875rem;
    }
  }
</style>

<i18n locale="en" lang="yaml">
videoCallExploration: A videocall works the same way as a Zoom meeting
candidateUrl: Link for candidate
adminUrl: Link for you and your colleagues
candidateLinkCopied: Candidate link copied
adminLinkCopied: Your link copied
videoCallBottomText: 'The candidate cannot start and stop recording the meeting, end the meeting, and does not have access to some other features'
</i18n>

<i18n locale="ru" lang="yaml">
videoCallExploration: Видеозвонок работает также, как встреча в Zoom
candidateUrl: Ссылка для кандидат
adminUrl: Ссылка для себя и коллег
candidateLinkCopied: Ссылка кандидата скопирована
adminLinkCopied: Ваша ссылка скопирована
videoCallBottomText: 'Кандидат не может начать и остановить запись встречи, закончить встречу и не имеет доступа к некоторым другим функциям'
</i18n>
